/* Auto rate rules for the tile price list. Type one anchor PREM rate, the rest follow.
   All rates are per sq.ft. Any rate can still be changed by hand afterwards. */

const r2 = (n) => Math.round(n * 100) / 100;
const has = (s, re) => re.test(String(s || '').toLowerCase());

/** '600x600' | '1200x600' | '' from a section heading such as "600x600 mm DOUBLE CHARGED (2x2)". */
export function sizeKind(heading) {
  const h = String(heading || '').toLowerCase().replace(/\s+/g, '');
  if (/(1200x600|600x1200)/.test(h)) return '1200x600';
  if (/600x600/.test(h)) return '600x600';
  return '';
}

/** Which rule role a series name plays inside a size. '' when the series is not part of the rules. */
export function roleOf(kind, seriesName) {
  const n = String(seriesName || '').toLowerCase();
  if (kind === '600x600') {
    if (has(n, /^\s*pro\b/)) return 'pro';
    if (has(n, /melody\s*dark|plain/)) return 'dark';
    if (has(n, /amazon|delta/)) return 'amazon';
    if (has(n, /melody\s*light/)) return 'mllight';
    return '';
  }
  if (kind === '1200x600') {
    if (has(n, /alaska/)) return 'alaska';
    if (has(n, /(delta|marble|dessert)\s*dark/)) return 'groupB';
    if (has(n, /melody\s*dark|(delta|marble|dessert)\s*light/)) return 'groupA';
    if (has(n, /melody\s*light/)) return 'mllight';
    return '';
  }
  return '';
}

/** Roles whose PREM rate drives all the others. */
export function isAnchor(kind, role) {
  if (kind === '600x600') return role === 'pro' || role === 'mllight';
  if (kind === '1200x600') return role === 'mllight';
  return false;
}

/** { role: { p1: PREM, p2: STD } } for every role of the size, from one typed anchor PREM rate. */
export function deriveRates(kind, anchorRole, anchorPre) {
  const v = Number(anchorPre);
  if (!Number.isFinite(v) || !isAnchor(kind, anchorRole)) return null;
  if (kind === '600x600') {
    const ml = anchorRole === 'mllight' ? v : v + 1;
    const pro = anchorRole === 'pro' ? v : v - 1;
    const dark = ml + 3;
    return {
      pro: { p1: r2(pro), p2: r2(pro - 2) },
      mllight: { p1: r2(ml), p2: r2(ml - 2) },
      dark: { p1: r2(dark), p2: r2(dark - 2) },
      amazon: { p1: r2(dark), p2: r2(dark - 2) },
    };
  }
  const ml = v;
  const a = ml + 3;
  const b = a + 3;
  const al = b - 1;
  return {
    mllight: { p1: r2(ml), p2: r2(ml - 3) },
    groupA: { p1: r2(a), p2: r2(a - 3) },
    groupB: { p1: r2(b), p2: r2(b - 3) },
    alaska: { p1: r2(al), p2: r2(al - 3) },
  };
}

/**
 * Applies a typed PREM rate on group `gi` to the whole section.
 * `manual` is a Set of "role:p1|p2" cells the user fixed by hand - those are left alone.
 * Returns the new groups array, or null when the typed group is not an anchor.
 */
export function applyAnchor(section, gi, value, manual) {
  const kind = sizeKind(section.heading);
  const role = roleOf(kind, section.groups[gi] && section.groups[gi].series_name);
  const rates = deriveRates(kind, role, value);
  if (!rates) return null;
  return section.groups.map((g, i) => {
    const r = roleOf(kind, g.series_name);
    const d = rates[r];
    if (!d) return g;
    const keep1 = i !== gi && manual && manual.has(`${r}:p1`);
    const keep2 = manual && manual.has(`${r}:p2`);
    return { ...g, price1: i === gi ? value : keep1 ? g.price1 : d.p1, price2: keep2 ? g.price2 : d.p2 };
  });
}
